import React, { useContext, useState, useRef, useEffect } from "react";

import { makeStyles } from "@material-ui/core/styles";
import { IconButton } from "@material-ui/core";
import { Replay, EventNote, SwapHoriz, Delete, LocalOffer } from "@material-ui/icons";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ConfirmationModal from "../ConfirmationModal";
import TransferTicketModalCustom from "../TransferTicketModalCustom";
import ScheduleModal from "../ScheduleModal";
import TagsModal from "../TagsModal";
import ButtonWithSpinner from "../ButtonWithSpinner";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import UndoRoundedIcon from '@material-ui/icons/UndoRounded';
import Tooltip from '@material-ui/core/Tooltip';


const useStyles = makeStyles(theme => ({
	actionButtons: {
		marginRight: 6,
		flex: "none",
		alignSelf: "center",
		marginLeft: "auto",
		display: "flex",
		alignItems: "center",
		gap: "6px",
		"& > *": {
			margin: 0,
		},
	},
	modernIconButton: {
		minWidth: "32px",
		height: "32px",
		borderRadius: "6px",
		border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e8e8e8'}`,
		backgroundColor: theme.mode === 'dark' ? '#374151' : '#fafafa',
		color: theme.mode === 'dark' ? '#d1d5db' : '#999999',
		transition: "all 0.2s ease",
		boxShadow: theme.mode === 'dark' 
			? '0 1px 2px rgba(0,0,0,0.2)' 
			: '0 1px 2px rgba(0,0,0,0.08)',
		"&:hover": {
			backgroundColor: theme.mode === 'dark' ? '#4B5563' : '#f0f0f0',
			transform: "translateY(-1px)",
			boxShadow: theme.mode === 'dark' 
				? '0 2px 4px rgba(0,0,0,0.3)' 
				: '0 2px 4px rgba(0,0,0,0.12)',
			borderColor: theme.mode === 'dark' ? '#6B7280' : '#d8d8d8',
			color: theme.mode === 'dark' ? '#e5e7eb' : '#777777',
		},
		"& svg": {
			fontSize: "16px",
		},
	},
}));

const TicketActionButtonsCustom = ({ ticket }) => {
	const classes = useStyles();
	const [loading, setLoading] = useState(false);
	const { user } = useContext(AuthContext);
	const { setCurrentTicket } = useContext(TicketsContext);
	const isMounted = useRef(true);

	// ✅ ESTADOS PARA LOS MODALES DE ACCIONES DIRECTAS
	const [confirmationOpen, setConfirmationOpen] = useState(false);
	const [transferTicketModalOpen, setTransferTicketModalOpen] = useState(false);
	const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
	const [tagsModalOpen, setTagsModalOpen] = useState(false);
	const [contactId, setContactId] = useState(null);

	// ✅ FUNCIONES PARA ACCIONES DIRECTAS
	const handleOpenScheduleModal = () => {
		setContactId(ticket.contact.id);
		setScheduleModalOpen(true);
	};

	const handleCloseScheduleModal = () => {
		setScheduleModalOpen(false);
		setContactId(null);
	};

	const handleOpenTransferModal = () => {
		setTransferTicketModalOpen(true);
	};

	const handleCloseTransferTicketModal = () => {
		if (isMounted.current) {
			setTransferTicketModalOpen(false);
		}
	};

	const handleOpenTagsModal = () => {
		setTagsModalOpen(true);
	};

	const handleCloseTagsModal = () => {
		if (isMounted.current) {
			setTagsModalOpen(false);
		}
	};

	const handleOpenConfirmationModal = () => {
		setConfirmationOpen(true);
	};

	const handleDeleteTicket = async () => {
		try {
			await api.delete(`/tickets/${ticket.id}`);
			
			setConfirmationOpen(false);
			
			// 🔄 ACTUALIZAR UI SIN RECARGAR PÁGINA
			if (window.location.pathname.includes(`/tickets/${ticket.id}`)) {
				window.location.href = "/tickets";
			}
			
		} catch (err) {
			toastError(err);
		}
	};

	const handleUpdateTicketStatus = async (e, status, userId) => {
		setLoading(true);
		try {
			await api.put(`/tickets/${ticket.id}`, {
				status: status,
				userId: userId || null,
				useIntegration: status === "closed" ? false : ticket.useIntegration,
				promptId: status === "closed" ? false : ticket.promptId,
				integrationId: status === "closed" ? false : ticket.integrationId
			});

			// ✅ VERIFICAR SI EL COMPONENTE ESTÁ MONTADO ANTES DE ACTUALIZAR ESTADO
			if (!isMounted.current) return;

		setLoading(false);
		if (status === "open") {
			setCurrentTicket({ ...ticket, code: "#open" });
		} else if (status === "closed") {
			// ✅ OPTIMIZADO: NO redirigir - confiar en eventos socket para actualizar UI
			// Esto previene la recarga de página y desconexión del socket
			setCurrentTicket({ id: null, code: null });
			// ✅ ELIMINADO: window.location.href = "/tickets" - Los eventos socket actualizarán la UI
		} else { // Este bloque manejará el status "pending" (y otros si los hubiera)
			// Aquí el ticket se movió a "pending", no redirigimos.
			// El componente Ticket (en Ticket/index.js) se actualizará vía Socket.IO
			setCurrentTicket({ id: null, code: null }); // Se puede mantener para limpiar el contexto del ticket seleccionado si aplica
		}
		} catch (err) {
			// ✅ VERIFICAR SI EL COMPONENTE ESTÁ MONTADO ANTES DE ACTUALIZAR ESTADO
			if (!isMounted.current) return;
			
			setLoading(false);
			toastError(err);
		}
	};

	// ✅ CLEANUP DEL COMPONENTE
	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	return (
		<>
		<div className={classes.actionButtons}>
			{ticket.status === "closed" && (
				<ButtonWithSpinner
					loading={loading}
					startIcon={<Replay />}
					size="small"
					onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}
				>
					{i18n.t("messagesList.header.buttons.reopen")}
				</ButtonWithSpinner>
			)}
			{ticket.status === "open" && (
				<>
					<Tooltip title={i18n.t("messagesList.header.buttons.return")}>
						<IconButton 
							onClick={e => handleUpdateTicketStatus(e, "pending", null)}
							className={classes.modernIconButton}
						>
							<UndoRoundedIcon />
						</IconButton>
					</Tooltip>
					<Tooltip title={i18n.t("messagesList.header.buttons.resolve")}>
						<IconButton 
							onClick={e => handleUpdateTicketStatus(e, "closed", user?.id)}
							className={classes.modernIconButton}
						>
							<CheckCircleIcon />
						</IconButton>
					</Tooltip>
					{/* ✅ BOTONES DE ACCIÓN DIRECTA - REEMPLAZANDO MENÚ DE TRES PUNTOS */}
					<Tooltip title={i18n.t("ticketOptionsMenu.schedule")}>
						<IconButton 
							onClick={handleOpenScheduleModal}
							className={classes.modernIconButton}
						>
							<EventNote />
						</IconButton>
					</Tooltip>
					<Tooltip title={i18n.t("ticketOptionsMenu.transfer")}>
						<IconButton 
							onClick={handleOpenTransferModal}
							className={classes.modernIconButton}
						>
							<SwapHoriz />
						</IconButton>
					</Tooltip>
					<Tooltip title="Etiquetas">
						<IconButton 
							onClick={handleOpenTagsModal}
							className={classes.modernIconButton}
						>
							<LocalOffer />
						</IconButton>
					</Tooltip>
					{/* ✅ Solo mostrar botón "Eliminar" para administradores */}
					{user?.profile === 'admin' && (
					<Tooltip title={i18n.t("ticketOptionsMenu.delete")}>
						<IconButton 
							onClick={handleOpenConfirmationModal}
							className={classes.modernIconButton}
						>
							<Delete />
						</IconButton>
					</Tooltip>
					)}
				</>
			)}
			{ticket.status === "pending" && (
				<ButtonWithSpinner
					loading={loading}
					size="small"
					variant="contained"
					color="primary"
					onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}
				>
					{i18n.t("messagesList.header.buttons.accept")}
				</ButtonWithSpinner>
			)}
		</div>
		{/* ✅ MODALES PARA LAS ACCIONES DIRECTAS */}
		<ConfirmationModal
			title={`${i18n.t("ticketOptionsMenu.confirmationModal.title")}${
				ticket.id
			} ${i18n.t("ticketOptionsMenu.confirmationModal.titleFrom")} ${
				ticket.contact?.name || i18n.t("ticketOptionsMenu.confirmationModal.unknownContact")
			}?`}
			open={confirmationOpen}
			onClose={setConfirmationOpen}
			onConfirm={handleDeleteTicket}
		>
			{i18n.t("ticketOptionsMenu.confirmationModal.message")}
		</ConfirmationModal>
		<TransferTicketModalCustom
			modalOpen={transferTicketModalOpen}
			onClose={handleCloseTransferTicketModal}
			ticketid={ticket.id}
		/>
		<ScheduleModal
			open={scheduleModalOpen}
			onClose={handleCloseScheduleModal}
			aria-labelledby="form-dialog-title"
			contactId={contactId}
			ticketId={ticket.id}
		/>
		<TagsModal
			open={tagsModalOpen}
			onClose={handleCloseTagsModal}
			ticketId={ticket.id}
			ticket={ticket}
		/>
	</>
	);
};

export default TicketActionButtonsCustom;
